<template>
  <v-container fluid class="pa-6">
    <!-- CABECERA DE LA PÁGINA -->
    <v-row class="mb-2" align="center">
      <v-col cols="12" md="5">
        <div class="d-flex align-center">
          <v-avatar color="primary lighten-5" size="48" class="mr-3">
            <v-icon color="primary">mdi-application-variable</v-icon>
          </v-avatar>
          <div>
            <h1 class="text-h5 font-weight-bold grey--text text--darken-3 mb-0">
              Variables por PLC
            </h1>
            <span class="text-caption grey--text">
              Monitoreo, edición masiva, rangos e importación
            </span>
          </div>
        </div>
      </v-col>

      <!-- Botones de Acción -->
      <v-col cols="12" md="7" class="text-md-right">
        <v-btn
          color="orange darken-2"
          dark
          depressed
          class="mr-2"
          :disabled="selectedVariables.length === 0"
          @click="openBulkEditModal"
        >
          <v-icon left small>mdi-table-edit</v-icon>
          Edición Masiva ({{ selectedVariables.length }})
        </v-btn>

        <v-btn
          color="primary"
          depressed
          class="mr-2"
          :disabled="!selectedPlcId"
          @click="openAddModal"
        >
          <v-icon left small>mdi-plus</v-icon>
          Nueva Variable
        </v-btn>

        <!-- Input invisible para archivo Excel -->
        <input
          ref="fileInput"
          type="file"
          accept=".xlsx, .xls, .csv"
          style="display: none"
          @change="handleFileUpload"
        />

        <v-btn
          color="teal"
          dark
          depressed
          :disabled="!selectedPlcId"
          @click="$refs.fileInput.click()"
        >
          <v-icon left small>mdi-file-excel</v-icon>
          Importar Excel
        </v-btn>
      </v-col>
    </v-row>

    <!-- BANNER: VARIABLES NUEVAS PENDIENTES -->
    <v-alert
      v-if="draftVariables.length > 0"
      type="warning"
      prominent
      border="left"
      class="mb-4 elevation-1"
    >
      <v-row align="center">
        <v-col class="grow">
          <div class="font-weight-bold subtitle-1">
            Hay {{ draftVariables.length }} variable(s) nueva(s) pendiente(s) de guardar.
          </div>
          <div class="caption">
            Revisa las filas destacadas en amarillo al inicio de la tabla. Sus valores ya se leen en tiempo real.
          </div>
        </v-col>
        <v-col class="shrink">
          <v-btn text color="grey darken-3" class="mr-2" @click="discardDrafts">
            Descartar
          </v-btn>
          <v-btn
            color="success"
            depressed
            :loading="savingDrafts"
            @click="saveAllNewVariables"
          >
            <v-icon left>mdi-content-save-all</v-icon>
            Guardar {{ draftVariables.length }} Nuevas
          </v-btn>
        </v-col>
      </v-row>
    </v-alert>

    <!-- BANNER: VARIABLES EDITADAS MANUALMENTE PENDIENTES -->
    <v-alert
      v-if="modifiedVariableIds.size > 0"
      type="info"
      prominent
      border="left"
      class="mb-4 elevation-1"
    >
      <v-row align="center">
        <v-col class="grow">
          <div class="font-weight-bold subtitle-1">
            Hay {{ modifiedVariableIds.size }} variable(s) con cambios pendientes.
          </div>
          <div class="caption">
            Has editado valores en la tabla. Guarda los cambios para persistirlos en la base de datos y SCADA.
          </div>
        </v-col>
        <v-col class="shrink">
          <v-btn text color="grey darken-3" class="mr-2" @click="discardTableEdits">
            Cancelar cambios
          </v-btn>
          <v-btn
            color="primary"
            depressed
            :loading="savingModified"
            @click="saveAllModifiedVariables"
          >
            <v-icon left>mdi-content-save</v-icon>
            Guardar {{ modifiedVariableIds.size }} Cambios
          </v-btn>
        </v-col>
      </v-row>
    </v-alert>

    <!-- SELECTOR DE PLC Y BUSCADOR CON SOPORTE DE INTERVALOS -->
    <v-card class="rounded-lg elevation-1 mb-6">
      <v-card-text class="py-4">
        <v-row align="center">
          <v-col cols="12" sm="6" md="4">
            <v-autocomplete
              v-model="selectedPlcId"
              :items="plcs"
              item-text="name"
              item-value="id_plc"
              label="Seleccionar PLC"
              prepend-inner-icon="mdi-developer-board"
              outlined
              dense
              hide-details
              placeholder="Elige un autómata..."
              @change="onPlcChange"
              :loading="loadingPlcs"
            >
              <template v-slot:item="{ item }">
                <v-list-item-content>
                  <v-list-item-title class="font-weight-medium">
                    {{ item.name }}
                  </v-list-item-title>
                  <v-list-item-subtitle class="text-caption grey--text">
                    ID: {{ item.id_plc }} | Topic: {{ item.topic_read || 'Sin topic' }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-autocomplete>
          </v-col>

          <!-- Buscador con filtro inteligente por texto o rango -->
          <v-col cols="12" sm="6" md="4">
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              append-outer-icon="mdi-filter-cog-outline"
              label="Buscar variable o rango (ej: D50 a D80)..."
              outlined
              dense
              hide-details
              clearable
              :disabled="!selectedPlcId"
              @click:append-outer="rangeDialog = true"
            ></v-text-field>

            <!-- Chip informativo cuando se detecta un rango activo -->
            <div v-if="activeRangeQuery" class="d-flex align-center mt-1">
              <v-chip
                x-small
                color="primary"
                outlined
                close
                class="font-weight-bold"
                @click:close="search = ''"
              >
                <v-icon left x-small>mdi-arrow-expand-horizontal</v-icon>
                Intervalo: {{ activeRangeQuery.fromRaw }} ➔ {{ activeRangeQuery.toRaw }}
                ({{ filteredVariables.length }} coincidentes)
              </v-chip>
            </div>
          </v-col>

          <v-col cols="12" md="4" class="d-flex align-center justify-md-end flex-wrap">
            <v-chip
              small
              outlined
              :color="client && client.connected ? 'success' : 'error'"
              class="font-weight-medium mr-2 my-1"
            >
              <v-icon left x-small>
                {{ client && client.connected ? 'mdi-wifi' : 'mdi-wifi-off' }}
              </v-icon>
              MQTT: {{ client && client.connected ? 'Conectado' : 'Desconectado' }}
            </v-chip>

            <v-chip
              v-if="currentPlc"
              small
              outlined
              color="primary"
              class="font-weight-medium my-1"
            >
              <v-icon left small>mdi-cloud-download-outline</v-icon>
              Topic: {{ currentPlc.topic_read || 'N/A' }}
            </v-chip>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- TABLA DE VARIABLES (ALIMENTADA POR filteredVariables) -->
    <v-card class="rounded-lg elevation-1">
      <v-data-table
        v-model="selectedVariables"
        show-select
        item-key="uid"
        :headers="headers"
        :items="filteredVariables"
        :loading="loadingVariables"
        :item-class="getRowClass"
        loading-text="Cargando variables..."
        no-data-text="No hay variables que coincidan con la búsqueda o rango"
        class="elevation-0"
        :items-per-page="15"
        :footer-props="{
          'items-per-page-options': [10, 15, 30, 50, -1],
          'items-per-page-text': 'Variables por página'
        }"
      >
        <template v-slot:no-data v-if="!selectedPlcId">
          <div class="py-8 text-center">
            <v-icon size="56" color="grey lighten-1">mdi-arrow-up-circle-outline</v-icon>
            <div class="text-body-1 font-weight-medium grey--text text--darken-1 mt-2">
              Por favor, selecciona un PLC para visualizar sus variables
            </div>
          </div>
        </template>

        <!-- Columna: ID / Estado -->
        <template v-slot:item.id="{ item }">
          <v-chip v-if="item.is_new" x-small color="amber darken-2" dark class="font-weight-bold">
            NUEVA
          </v-chip>
          <span v-else class="text-caption font-weight-bold grey--text text--darken-1">
            #{{ item.id }}
          </span>
          <v-chip v-if="modifiedVariableIds.has(item.id)" x-small color="blue" dark class="ml-1">
            Editada
          </v-chip>
        </template>

        <!-- Columna: Dirección con soporte para address y formatted_address -->
        <template v-slot:item.address="{ item }">
          <v-edit-dialog
            large
            persistent
            cancel-text="Cancelar"
            save-text="Aceptar"
            @open="onAddressEditOpen(item)"
            @save="onAddressEditSave(item)"
          >
            <div class="editable-cell d-flex flex-column justify-center">
              <div class="d-flex align-center">
                <code class="px-2 py-0.5 rounded primary--text font-weight-bold address-chip">
                  {{ item.address }}
                </code>
                <v-icon x-small class="ml-1 grey--text text--lighten-1 edit-hint-icon">mdi-pencil</v-icon>
              </div>

              <span
                v-if="item.formatted_address && normalizeAddress(item.formatted_address) !== normalizeAddress(item.address)"
                class="text-caption grey--text text--darken-2 font-family-monospace mt-0.5"
                style="font-size: 0.72rem !important;"
                title="Memoria de lectura FINS / MQTT"
              >
                PLC: <strong>{{ item.formatted_address }}</strong>
              </span>
            </div>

            <template v-slot:input>
              <div class="mt-3 font-weight-bold subtitle-2">Editar Direcciones de Memoria</div>
              
              <v-text-field
                v-model="editAddressBuffer.address"
                label="Dirección (Address)"
                dense
                autofocus
                class="mt-1"
                @input="onAddressBufferInput"
              ></v-text-field>

              <v-text-field
                v-model="editAddressBuffer.formatted_address"
                label="Dirección Formateada (Lectura PLC / MQTT)"
                dense
                hint="Esta dirección es la que realmente lee el FINS/MQTT"
                persistent-hint
              ></v-text-field>

              <div class="d-flex align-center mt-3 mb-2">
                <span class="caption grey--text mr-2">Valor en vivo (PLC):</span>
                <v-chip x-small label :color="getLiveValueFor(editAddressBuffer.formatted_address, editAddressBuffer.address) !== null ? 'blue-grey lighten-5' : 'grey lighten-4'">
                  <v-icon left x-small :color="getLiveValueFor(editAddressBuffer.formatted_address, editAddressBuffer.address) !== null ? 'success' : 'grey'" class="mr-1">mdi-circle</v-icon>
                  {{ formatLiveValue(getLiveValueFor(editAddressBuffer.formatted_address, editAddressBuffer.address)) }}
                </v-chip>
              </div>
            </template>
          </v-edit-dialog>
        </template>

        <!-- Columna: Descripción -->
        <template v-slot:item.description="{ item }">
          <v-edit-dialog
            :return-value.sync="item.description"
            large
            persistent
            cancel-text="Cancelar"
            save-text="Aceptar"
            @save="markAsModified(item)"
          >
            <div class="editable-cell d-flex align-center">
              <span class="font-weight-medium">{{ item.description || '-' }}</span>
              <v-icon x-small class="ml-1 grey--text text--lighten-1 edit-hint-icon">mdi-pencil</v-icon>
            </div>
            <template v-slot:input>
              <div class="mt-3 font-weight-bold subtitle-2">Editar Descripción</div>
              <v-text-field v-model="item.description" label="Descripción" single-line autofocus dense></v-text-field>
            </template>
          </v-edit-dialog>
        </template>

        <!-- Columna: Tipo -->
        <template v-slot:item.type="{ item }">
          <v-edit-dialog
            :return-value.sync="item.type"
            large
            persistent
            cancel-text="Cancelar"
            save-text="Aceptar"
            @save="markAsModified(item)"
          >
            <div class="editable-cell d-flex align-center">
              <v-chip small label outlined :color="getTypeColor(item.type)" class="text-uppercase font-weight-bold">
                {{ item.type || 'N/D' }}
              </v-chip>
              <v-icon x-small class="ml-1 grey--text text--lighten-1 edit-hint-icon">mdi-pencil</v-icon>
            </div>
            <template v-slot:input>
              <div class="mt-3 font-weight-bold subtitle-2">Seleccionar Tipo</div>
              <v-select v-model="item.type" :items="['REAL', 'BOOL', 'INT', 'DINT', 'WORD', 'STRING']" label="Tipo" dense></v-select>
            </template>
          </v-edit-dialog>
        </template>

        <!-- Columna: Modo R / W -->
        <template v-slot:item.read_write="{ item }">
          <v-edit-dialog
            :return-value.sync="item.read_write"
            large
            persistent
            cancel-text="Cancelar"
            save-text="Aceptar"
            @save="markAsModified(item)"
          >
            <div class="editable-cell d-flex align-center justify-center">
              <v-chip
                x-small
                label
                :color="item.read_write === 'W' || item.read_write === 'RW' ? 'amber lighten-4' : 'blue lighten-5'"
                :text-color="item.read_write === 'W' || item.read_write === 'RW' ? 'amber darken-4' : 'blue darken-4'"
                class="font-weight-bold"
              >
                {{ item.read_write || 'R' }}
              </v-chip>
            </div>
            <template v-slot:input>
              <v-select v-model="item.read_write" :items="['R', 'W', 'RW']" label="Modo" dense></v-select>
            </template>
          </v-edit-dialog>
        </template>

        <!-- Columna: Valor Actual -->
        <template v-slot:item.value="{ item }">
          <div class="d-flex align-center justify-end">
            <v-chip
              small
              label
              :color="item.value !== null && item.value !== undefined ? 'blue-grey lighten-5' : 'grey lighten-4'"
              :text-color="item.value !== null && item.value !== undefined ? 'primary' : 'grey'"
              class="font-weight-bold font-family-monospace px-3 live-chip"
            >
              <v-icon left x-small :color="item.value !== null && item.value !== undefined ? 'success' : 'grey lighten-1'" class="mr-1">
                mdi-circle
              </v-icon>
              {{ formatLiveValue(item.value) }}
            </v-chip>
          </div>
        </template>

        <!-- Columna: Histórico / Segundos -->
        <template v-slot:item.history="{ item }">
          <v-edit-dialog
            :return-value.sync="item.segundos"
            large
            persistent
            cancel-text="Cancelar"
            save-text="Aceptar"
            @save="markAsModified(item)"
          >
            <div class="editable-cell d-flex align-center justify-center">
              <v-chip
                small
                :color="hasHistory(item) ? 'teal lighten-5' : 'grey lighten-4'"
                :text-color="hasHistory(item) ? 'teal darken-2' : 'grey darken-1'"
                class="font-weight-medium"
              >
                <v-icon left x-small :color="hasHistory(item) ? 'teal darken-2' : 'grey darken-1'">
                  {{ hasHistory(item) ? 'mdi-clock-check-outline' : 'mdi-minus-circle-outline' }}
                </v-icon>
                {{ hasHistory(item) ? `${item.segundos} s` : 'Sin histórico' }}
              </v-chip>
            </div>
            <template v-slot:input>
              <v-text-field v-model.number="item.segundos" label="Segundos de histórico" type="number" min="0" dense></v-text-field>
            </template>
          </v-edit-dialog>
        </template>

        <!-- Columna: Alarma Asociada -->
        <template v-slot:item.alarm="{ item }">
          <v-chip
            v-if="item.alarm_value"
            small
            color="red lighten-5"
            text-color="red darken-3"
            class="font-weight-medium"
          >
            <v-icon left x-small color="red">mdi-bell-alert</v-icon>
            Val: {{ item.alarm_value !== null && item.alarm_value !== undefined ? item.alarm_value : 'Activa' }}
          </v-chip>
          <span v-else class="grey--text text--lighten-1">—</span>
        </template>

        <!-- Columna: Color -->
        <template v-slot:item.color="{ item }">
          <v-menu offset-y :close-on-content-click="false">
            <template v-slot:activator="{ on }">
              <div class="editable-cell d-flex align-center" v-on="on">
                <span class="color-circle mr-2" :style="{ backgroundColor: item.color || '#cccccc' }"></span>
                <span class="text-caption font-family-monospace font-weight-medium">{{ item.color || '-' }}</span>
              </div>
            </template>
            <v-card class="pa-2">
              <v-color-picker v-model="item.color" mode="hexa" hide-mode-switch />
              <v-card-actions class="px-0 pb-0">
                <v-spacer></v-spacer>
                <v-btn small depressed color="primary" @click="markAsModified(item)">Aceptar</v-btn>
              </v-card-actions>
            </v-card>
          </v-menu>
        </template>

        <!-- Columna: Activa -->
        <template v-slot:item.active="{ item }">
          <div class="d-flex justify-center align-center">
            <v-switch
              v-model="item.active"
              color="success"
              dense
              hide-details
              class="ma-0 pa-0"
              @change="markAsModified(item)"
            ></v-switch>
          </div>
        </template>

        <!-- Columna: Acciones -->
        <template v-slot:item.actions="{ item }">
          <v-btn
            v-if="item.is_new"
            icon
            small
            color="error"
            title="Quitar de la lista previa"
            @click="removeDraft(item)"
          >
            <v-icon small>mdi-close-circle-outline</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <!-- DIÁLOGO ASISTIDO PARA FILTRAR POR RANGO -->
    <v-dialog v-model="rangeDialog" max-width="450px">
      <v-card class="rounded-lg">
        <v-card-title class="headline primary white--text py-3">
          <v-icon left color="white">mdi-arrow-expand-horizontal</v-icon>
          Filtrar por Rango de Memoria
        </v-card-title>
        <v-card-text class="pt-4">
          <p class="caption grey--text text--darken-1 mb-3">
            Especifica el inicio y fin del intervalo (ej. D50 a D80, D1000.00 a D1005.15 o DB1.DBD0 a DB1.DBD20):
          </p>
          <v-row dense>
            <v-col cols="6">
              <v-text-field
                v-model="rangeFrom"
                label="Desde"
                outlined
                dense
                placeholder="D50"
              ></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model="rangeTo"
                label="Hasta"
                outlined
                dense
                placeholder="D80"
              ></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="px-5 pb-4">
          <v-spacer></v-spacer>
          <v-btn text @click="rangeDialog = false">Cerrar</v-btn>
          <v-btn color="primary" depressed @click="applyRangeDialog">Aplicar Filtro</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- DIÁLOGO DE EDICIÓN MASIVA -->
    <v-dialog v-model="bulkModal" max-width="650px" persistent>
      <v-card class="rounded-lg">
        <v-card-title class="headline orange lighten-5 orange--text text--darken-4 py-3">
          <v-icon left color="orange darken-3">mdi-table-edit</v-icon>
          Edición Masiva ({{ selectedVariables.length }} seleccionadas)
        </v-card-title>

        <v-card-text class="pt-4">
          <p class="text-body-2 grey--text text--darken-2 mb-3">
            Marca únicamente los campos que desees sobrescribir en todas las variables seleccionadas:
          </p>

          <v-list dense class="pa-0">
            <v-list-item class="px-0">
              <v-row align="center">
                <v-col cols="4">
                  <v-checkbox v-model="bulkFields.type.apply" label="Modificar Tipo" hide-details dense></v-checkbox>
                </v-col>
                <v-col cols="8">
                  <v-select
                    v-model="bulkFields.type.value"
                    :disabled="!bulkFields.type.apply"
                    :items="['REAL', 'BOOL', 'INT', 'DINT', 'WORD', 'STRING']"
                    outlined
                    dense
                    hide-details
                    placeholder="Elige tipo"
                  ></v-select>
                </v-col>
              </v-row>
            </v-list-item>

            <v-list-item class="px-0">
              <v-row align="center">
                <v-col cols="4">
                  <v-checkbox v-model="bulkFields.read_write.apply" label="Modo R/W" hide-details dense></v-checkbox>
                </v-col>
                <v-col cols="8">
                  <v-select
                    v-model="bulkFields.read_write.value"
                    :disabled="!bulkFields.read_write.apply"
                    :items="['R', 'W', 'RW']"
                    outlined
                    dense
                    hide-details
                  ></v-select>
                </v-col>
              </v-row>
            </v-list-item>

            <v-list-item class="px-0">
              <v-row align="center">
                <v-col cols="4">
                  <v-checkbox v-model="bulkFields.segundos.apply" label="Histórico (s)" hide-details dense></v-checkbox>
                </v-col>
                <v-col cols="8">
                  <v-text-field
                    v-model.number="bulkFields.segundos.value"
                    :disabled="!bulkFields.segundos.apply"
                    type="number"
                    min="0"
                    placeholder="0 = Sin histórico"
                    outlined
                    dense
                    hide-details
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-list-item>

            <v-list-item class="px-0">
              <v-row align="center">
                <v-col cols="4">
                  <v-checkbox v-model="bulkFields.active.apply" label="Estado Activa" hide-details dense></v-checkbox>
                </v-col>
                <v-col cols="8">
                  <v-switch
                    v-model="bulkFields.active.value"
                    :disabled="!bulkFields.active.apply"
                    :label="bulkFields.active.value ? 'Activas' : 'Inactivas'"
                    dense
                    hide-details
                    color="success"
                  ></v-switch>
                </v-col>
              </v-row>
            </v-list-item>
          </v-list>
        </v-card-text>

        <v-card-actions class="px-5 pb-4">
          <v-spacer></v-spacer>
          <v-btn text @click="bulkModal = false">Cancelar</v-btn>
          <v-btn
            color="orange darken-2"
            dark
            depressed
            :loading="savingBulk"
            @click="applyBulkEdit"
          >
            Aplicar y Guardar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- MODAL AÑADIR MANUAL -->
    <v-dialog v-model="addModal" max-width="600px" persistent>
      <v-card class="rounded-lg">
        <v-card-title class="headline grey lighten-4 py-3">
          <v-icon left color="primary">mdi-plus-box-outline</v-icon>
          Nueva Variable (PLC: {{ currentPlc ? currentPlc.name : '' }})
        </v-card-title>
        <v-card-text class="pt-4">
          <v-row dense>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="newVar.address"
                label="Dirección*"
                outlined
                dense
                placeholder="Ej: DM52 o 3804"
                required
                @input="onNewVarAddressInput"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="newVar.formatted_address"
                label="Dirección Formateada (PLC / MQTT)*"
                outlined
                dense
                placeholder="Ej: D52 o CIO3804"
                hint="Dirección real de lectura en FINS"
                persistent-hint
              ></v-text-field>
            </v-col>

            <v-col cols="12" v-if="newVar.formatted_address || newVar.address" class="mt-n2 mb-2">
              <div class="d-flex align-center">
                <v-icon x-small :color="getLiveValueFor(newVar.formatted_address, newVar.address) !== null ? 'success' : 'grey'" class="mr-1">mdi-circle</v-icon>
                <span class="caption grey--text mr-1">Valor en vivo en PLC:</span>
                <strong class="caption font-family-monospace primary--text">
                  {{ formatLiveValue(getLiveValueFor(newVar.formatted_address, newVar.address)) }}
                </strong>
              </div>
            </v-col>

            <v-col cols="12">
              <v-text-field v-model="newVar.description" label="Descripción*" outlined dense placeholder="Ej: Nivel depósito agua"></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-select v-model="newVar.type" :items="['REAL', 'BOOL', 'INT', 'DINT', 'WORD', 'STRING']" label="Tipo*" outlined dense></v-select>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field v-model.number="newVar.segundos" label="Segundos de Histórico" type="number" min="0" outlined dense hint="Vacío o 0 = Sin histórico" persistent-hint></v-text-field>
            </v-col>

            <v-col cols="12" class="mt-2">
              <v-checkbox v-model="newVar.is_alarm" label="¿Genera alarma?" dense hide-details class="ma-0"></v-checkbox>
            </v-col>
            <v-col cols="12" sm="6" v-if="newVar.is_alarm">
              <v-checkbox v-model="newVar.alarm_value" label="Valor de Alarma" dense hide-details class="ma-0"></v-checkbox>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="px-5 pb-4">
          <v-spacer></v-spacer>
          <v-btn text @click="addModal = false">Cancelar</v-btn>
          <v-btn color="primary" depressed @click="addSingleDraft">Añadir a la lista</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- NOTIFICACIONES -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3500" top right>
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">Cerrar</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { mapState, mapActions } from "vuex";
import * as XLSX from "xlsx";

export default {
  name: "VariablesView",
  data() {
    return {
      selectedPlcId: null,
      plcs: [],
      variables: [],
      search: "",

      // Diálogo asistido de rango
      rangeDialog: false,
      rangeFrom: "",
      rangeTo: "",

      // Mapa reactivo de lecturas en tiempo real
      latestMemoryValues: {},

      editAddressBuffer: {
        address: "",
        formatted_address: "",
      },

      selectedVariables: [],
      modifiedVariableIds: new Set(),
      savingModified: false,

      bulkModal: false,
      savingBulk: false,
      bulkFields: {
        type: { apply: false, value: "REAL" },
        read_write: { apply: false, value: "R" },
        segundos: { apply: false, value: 0 },
        active: { apply: false, value: true },
      },

      currentSubscribedTopic: null,

      loadingPlcs: false,
      loadingVariables: false,
      savingDrafts: false,

      addModal: false,
      newVar: {
        address: "",
        formatted_address: "",
        description: "",
        type: "REAL",
        read_write: "R",
        segundos: null,
        color: null,
        is_alarm: false,
        alarm_value: true,
      },

      snackbar: false,
      snackbarText: "",
      snackbarColor: "info",

      headers: [
        { text: "ID", value: "id", width: "85px", align: "start" },
        { text: "Dirección", value: "address", width: "160px" },
        { text: "Descripción", value: "description" },
        { text: "Tipo", value: "type", width: "95px" },
        { text: "Modo", value: "read_write", width: "75px", align: "center" },
        { text: "Valor Actual", value: "value", width: "135px", align: "end" },
        { text: "Histórico", value: "history", width: "130px", align: "center" },
        { text: "Alarma", value: "alarm", width: "130px", align: "center" },
        { text: "Color", value: "color", width: "115px" },
        { text: "Activa", value: "active", width: "80px", align: "center" },
        { text: "", value: "actions", width: "50px", align: "center", sortable: false },
      ],
    };
  },

  computed: {
    ...mapState("nodered", ["client"]),

    currentPlc() {
      return this.plcs.find((p) => p.id_plc === this.selectedPlcId) || null;
    },

    draftVariables() {
      return this.variables.filter((v) => v.is_new === true);
    },

    // -------------------------------------------------------------
    // MOTOR DE BÚSQUEDA Y FILTRADO (POR TEXTO O POR INTERVALO)
    // -------------------------------------------------------------
    activeRangeQuery() {
      return this.parseRangeQuery(this.search);
    },

    filteredVariables() {
      if (!this.search || !this.search.trim()) {
        return this.variables;
      }

      // Si el texto del buscador es un rango válido (ej: D50 a D80 o D1000.00 a D1005.15)
      const range = this.activeRangeQuery;
      if (range) {
        return this.variables.filter((v) => this.isVariableInRange(v, range));
      }

      // De lo contrario, búsqueda normal por texto estándar
      const q = this.search.toLowerCase().trim();
      return this.variables.filter((v) => {
        const addr = (v.address || "").toLowerCase();
        const fmt = (v.formatted_address || "").toLowerCase();
        const desc = (v.description || "").toLowerCase();
        const type = (v.type || "").toLowerCase();
        const id = String(v.id || "").toLowerCase();

        return (
          addr.includes(q) ||
          fmt.includes(q) ||
          desc.includes(q) ||
          type.includes(q) ||
          id.includes(q)
        );
      });
    },
  },

  watch: {
    "client.connected": {
      immediate: true,
      handler(newVal) {
        if (newVal && this.currentPlc && this.currentPlc.topic_read) {
          this.listenToPlcMqtt(this.currentPlc.topic_read);
        }
      },
    },
  },

  async mounted() {
    try {
      await this.fetchPlcs();
    } catch (e) {
      console.error("Error al cargar PLCs iniciales:", e);
    } finally {
      this.createConnection();
    }
  },

  beforeDestroy() {
    if (this.currentSubscribedTopic) {
      this.doUnsubscribe(this.currentSubscribedTopic);
    }
    if (this.client) {
      this.client.removeAllListeners("message");
    }
  },

  methods: {
    ...mapActions(["getData", "putData", "postData"]),
    ...mapActions("nodered", ["createConnection", "doSubscribe", "doUnsubscribe"]),

    showNotification(text, color = "info") {
      this.snackbarText = text;
      this.snackbarColor = color;
      this.snackbar = true;
    },

    getRowClass(item) {
      if (item.is_new) return "new-draft-row";
      if (this.modifiedVariableIds.has(item.id)) return "modified-row";
      return "";
    },

    // -------------------------------------------------------------
    // PARSER UNIVERSAL DE DIRECCIONES PLC (OMRON Y SIEMENS)
    // -------------------------------------------------------------
    parsePlcAddress(raw) {
      if (!raw) return null;
      let str = String(raw).trim().toUpperCase().replace(/\s+/g, "");

      // Normalizaciones comunes entre marcas
      if (str.startsWith("DM")) str = "D" + str.slice(2);
      str = str.replace(",REAL", ".DBD").replace(",X", ".DBX").replace(",INT", ".DBW");

      // 1. Detección de Bits (ej: D1000.00, CIO3944.05, DB1.DBX0.0, M0.0)
      const bitMatch = str.match(/^([A-Z0-9_,\.]*?[A-Z_,\.]+)(\d+)\.(\d+)$/i);
      if (bitMatch) {
        let prefix = bitMatch[1];
        const wordOrByte = parseInt(bitMatch[2], 10);
        const bit = parseInt(bitMatch[3], 10);

        // Siemens numera bits 0..7; Omron numera bits 0..15 por palabra
        const isSiemens = prefix.includes("DBX") || ["M", "I", "Q", "E", "A"].includes(prefix);
        const multiplier = isSiemens ? 8 : 16;

        return {
          prefix,
          isBit: true,
          mainNum: wordOrByte,
          bitNum: bit,
          value: wordOrByte * multiplier + bit,
        };
      }

      // 2. Detección de Palabras / Números (ej: D50, CIO3804, DB1.DBD20, MW10)
      const wordMatch = str.match(/^([A-Z0-9_,\.]*?[A-Z_,\.]+)(\d+)$/i);
      if (wordMatch) {
        let prefix = wordMatch[1];
        const num = parseInt(wordMatch[2], 10);

        return {
          prefix,
          isBit: false,
          mainNum: num,
          bitNum: null,
          value: num,
        };
      }

      return null;
    },

    // Detecta si el texto introducido corresponde a un intervalo (ej: D50 a D80 o D50 - D80)
    parseRangeQuery(query) {
      if (!query || typeof query !== "string") return null;
      const trimmed = query.trim();

      // Reconoce separadores: " a ", " to ", "..", " - "
      const rangeRegex = /^(.*?)(?:\s+(?:a|to)\s+|\s*\.\.\s*|\s+-\s+)(.*?)$/i;
      const match = trimmed.match(rangeRegex);
      if (!match) return null;

      const fromRaw = match[1].trim();
      const toRaw = match[2].trim();

      const fromParsed = this.parsePlcAddress(fromRaw);
      const toParsed = this.parsePlcAddress(toRaw);

      if (!fromParsed || !toParsed) return null;

      // El prefijo y el tipo (bit vs palabra) deben ser compatibles
      if (fromParsed.prefix !== toParsed.prefix || fromParsed.isBit !== toParsed.isBit) {
        return null;
      }

      return {
        prefix: fromParsed.prefix,
        isBit: fromParsed.isBit,
        min: Math.min(fromParsed.value, toParsed.value),
        max: Math.max(fromParsed.value, toParsed.value),
        fromRaw,
        toRaw,
      };
    },

    // Evalúa si una variable cae dentro del rango numérico calculado
    isVariableInRange(v, range) {
      const candidates = [v.formatted_address, v.address].filter(Boolean);

      for (const cand of candidates) {
        const parsed = this.parsePlcAddress(cand);
        if (parsed) {
          if (
            parsed.prefix === range.prefix &&
            parsed.isBit === range.isBit &&
            parsed.value >= range.min &&
            parsed.value <= range.max
          ) {
            return true;
          }
        }
      }
      return false;
    },

    applyRangeDialog() {
      if (!this.rangeFrom || !this.rangeTo) {
        this.showNotification("Indica el inicio y fin del intervalo", "warning");
        return;
      }
      this.search = `${this.rangeFrom.trim()} a ${this.rangeTo.trim()}`;
      this.rangeDialog = false;
    },

    // Normaliza una dirección a mayúsculas y sin espacios
    normalizeAddress(addr) {
      if (!addr) return "";
      return String(addr).toUpperCase().replace(/\s+/g, "").trim();
    },

    computeFormattedAddress(raw) {
      if (!raw) return "";
      let addr = String(raw).trim().toUpperCase().replace(/\s+/g, "");

      if (addr.startsWith("DM")) {
        addr = "D" + addr.slice(2);
      } else if (/^\d+(\.\d+)?$/.test(addr)) {
        addr = "CIO" + addr;
      }
      addr = addr.replace(/^D0+(\d+)/, "D$1");

      return addr;
    },

    getLiveValueFor(formattedAddr, addr) {
      const cleanFmt = this.normalizeAddress(formattedAddr);
      if (cleanFmt && this.latestMemoryValues[cleanFmt] !== undefined) {
        return this.latestMemoryValues[cleanFmt];
      }

      const cleanAddr = this.normalizeAddress(addr);
      if (cleanAddr && this.latestMemoryValues[cleanAddr] !== undefined) {
        return this.latestMemoryValues[cleanAddr];
      }

      return null;
    },

    // -------------------------------------------------------------
    // EDICIÓN EN LÍNEA: GESTIÓN DE ADDRESS Y FORMATTED_ADDRESS
    // -------------------------------------------------------------
    onAddressEditOpen(item) {
      this.editAddressBuffer = {
        address: item.address,
        formatted_address: item.formatted_address || this.computeFormattedAddress(item.address),
      };
    },

    onAddressBufferInput(val) {
      this.editAddressBuffer.formatted_address = this.computeFormattedAddress(val);
    },

    onAddressEditSave(item) {
      item.address = String(this.editAddressBuffer.address).trim();
      item.formatted_address = String(this.editAddressBuffer.formatted_address || item.address).trim();

      item.value = null;
      const cached = this.getLiveValueFor(item.formatted_address, item.address);
      if (cached !== null) {
        this.$set(item, "value", cached);
      }

      this.markAsModified(item);
    },

    markAsModified(item) {
      if (item.is_new) return;
      this.modifiedVariableIds.add(item.id);
      this.modifiedVariableIds = new Set(this.modifiedVariableIds);
    },

    discardTableEdits() {
      this.modifiedVariableIds.clear();
      this.modifiedVariableIds = new Set();
      this.fetchVariables(this.selectedPlcId);
      this.showNotification("Cambios descartados", "info");
    },

    async saveAllModifiedVariables() {
      if (this.modifiedVariableIds.size === 0) return;

      this.savingModified = true;
      try {
        const payload = this.variables
          .filter((v) => this.modifiedVariableIds.has(v.id))
          .map((item) => ({
            id: item.id,
            address: item.address,
            formatted_address: item.formatted_address || item.address,
            description: item.description,
            type: item.type,
            read_write: item.read_write || "R",
            segundos: item.segundos ? Number(item.segundos) : null,
            color: item.color || null,
            active: Boolean(item.active),
          }));

        await this.putData({
          path: "variables/batch",
          body: { variables: payload },
        });

        this.showNotification(`¡${payload.length} variable(s) actualizadas con éxito!`, "success");
        this.modifiedVariableIds.clear();
        this.modifiedVariableIds = new Set();
      } catch (error) {
        console.error("Error al guardar variables modificadas:", error);
        this.showNotification("Error al guardar los cambios", "error");
      } finally {
        this.savingModified = false;
      }
    },

    // -------------------------------------------------------------
    // EDICIÓN MASIVA
    // -------------------------------------------------------------
    openBulkEditModal() {
      this.bulkFields = {
        type: { apply: false, value: "REAL" },
        read_write: { apply: false, value: "R" },
        segundos: { apply: false, value: 0 },
        active: { apply: false, value: true },
      };
      this.bulkModal = true;
    },

    async applyBulkEdit() {
      const anyFieldSelected = Object.values(this.bulkFields).some((f) => f.apply);
      if (!anyFieldSelected) {
        this.showNotification("Selecciona al menos un campo a modificar", "warning");
        return;
      }

      this.savingBulk = true;
      try {
        const payload = [];

        for (const item of this.selectedVariables) {
          if (this.bulkFields.type.apply) item.type = this.bulkFields.type.value;
          if (this.bulkFields.read_write.apply) item.read_write = this.bulkFields.read_write.value;
          if (this.bulkFields.segundos.apply) item.segundos = Number(this.bulkFields.segundos.value);
          if (this.bulkFields.active.apply) item.active = Boolean(this.bulkFields.active.value);

          if (!item.is_new) {
            payload.push({
              id: item.id,
              address: item.address,
              formatted_address: item.formatted_address || item.address,
              description: item.description,
              type: item.type,
              read_write: item.read_write,
              segundos: item.segundos ? Number(item.segundos) : null,
              color: item.color,
              active: Boolean(item.active),
            });
          }
        }

        if (payload.length > 0) {
          await this.putData({
            path: "variables/batch",
            body: { variables: payload },
          });
        }

        this.showNotification(
          `¡Edición masiva completada para ${this.selectedVariables.length} variable(s)!`,
          "success"
        );

        this.bulkModal = false;
        this.selectedVariables = [];
      } catch (error) {
        console.error("Error al procesar la edición masiva:", error);
        this.showNotification("Error al procesar la edición masiva", "error");
      } finally {
        this.savingBulk = false;
      }
    },

    // -------------------------------------------------------------
    // CREACIÓN MANUAL Y EXCEL
    // -------------------------------------------------------------
    openAddModal() {
      this.newVar = {
        address: "",
        formatted_address: "",
        description: "",
        type: "REAL",
        read_write: "R",
        segundos: null,
        color: null,
        is_alarm: false,
        alarm_value: true,
      };
      this.addModal = true;
    },

    onNewVarAddressInput(val) {
      this.newVar.formatted_address = this.computeFormattedAddress(val);
    },

    addSingleDraft() {
      if (!this.newVar.address || !this.newVar.description) {
        this.showNotification("La dirección y la descripción son obligatorias", "warning");
        return;
      }

      const address = String(this.newVar.address).trim();
      const formatted_address = String(this.newVar.formatted_address || this.computeFormattedAddress(address)).trim();

      const draftItem = {
        ...this.newVar,
        address,
        formatted_address,
        uid: "draft_" + Date.now() + "_" + Math.random(),
        is_new: true,
        active: true,
        value: this.getLiveValueFor(formatted_address, address),
      };

      this.variables.unshift(draftItem);
      this.addModal = false;
      this.showNotification("Variable añadida a la vista previa. Ya recibiendo valor en tiempo real", "info");
    },

    handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const rows = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });

          const newDrafts = [];
          const startIndex =
            isNaN(rows[0]?.[4]) &&
            typeof rows[0]?.[0] === "string" &&
            rows[0][0].toLowerCase().includes("address")
              ? 1
              : 0;

          for (let i = startIndex; i < rows.length; i++) {
            const row = rows[i];
            if (!row || !row[0]) continue;

            const address = String(row[0]).trim();
            const formatted_address = row[1]
              ? String(row[1]).trim()
              : this.computeFormattedAddress(address);

            const description = row[2] ? String(row[2]).trim() : "";
            const type = row[3] ? String(row[3]).trim().toUpperCase() : "REAL";
            const segundos =
              row[4] !== undefined && row[4] !== null && row[4] !== "" ? Number(row[4]) : null;

            const hasAlarm = row[5] !== undefined && row[5] !== null && String(row[5]).trim() !== "";
            const alarm_value = hasAlarm && row[6] !== undefined ? row[6] : null;

            newDrafts.push({
              uid: "excel_" + i + "_" + Date.now(),
              address,
              formatted_address,
              description,
              type,
              read_write: "R",
              segundos,
              color: null,
              is_alarm: hasAlarm,
              alarm_value,
              active: true,
              is_new: true,
              value: this.getLiveValueFor(formatted_address, address),
            });
          }

          if (newDrafts.length === 0) {
            this.showNotification("No se encontraron filas válidas en el archivo Excel", "warning");
            return;
          }

          this.variables = [...newDrafts, ...this.variables];
          this.showNotification(`Se cargaron ${newDrafts.length} variables en vista previa.`, "info");
        } catch (error) {
          console.error("Error al procesar el archivo Excel:", error);
          this.showNotification("Error al procesar el archivo Excel", "error");
        } finally {
          event.target.value = "";
        }
      };

      reader.readAsArrayBuffer(file);
    },

    removeDraft(item) {
      const idx = this.variables.indexOf(item);
      if (idx !== -1) {
        this.variables.splice(idx, 1);
      }
    },

    discardDrafts() {
      this.variables = this.variables.filter((v) => !v.is_new);
      this.showNotification("Variables en vista previa descartadas", "info");
    },

    async saveAllNewVariables() {
      if (this.draftVariables.length === 0) return;

      this.savingDrafts = true;
      try {
        const payload = {
          plc_id: this.selectedPlcId,
          variables: this.draftVariables.map((v) => ({
            address: v.address,
            formatted_address: v.formatted_address || v.address,
            description: v.description,
            type: v.type,
            read_write: v.read_write || "R",
            segundos: v.segundos ? Number(v.segundos) : null,
            color: v.color || null,
            active: v.active !== false,
            is_alarm: Boolean(v.is_alarm),
            alarm_value: v.alarm_value,
          })),
        };

        await this.postData({
          path: "variables/batch",
          body: payload,
        });

        this.showNotification("¡Variables guardadas con éxito en la base de datos!", "success");
        await this.fetchVariables(this.selectedPlcId);
      } catch (error) {
        console.error("Error al guardar variables por lotes:", error);
        this.showNotification("Error al guardar las nuevas variables", "error");
      } finally {
        this.savingDrafts = false;
      }
    },

    // -------------------------------------------------------------
    // ESCUCHA Y ENRUTAMIENTO MULTIPROTOCOLO MQTT (OMRON / SIEMENS)
    // -------------------------------------------------------------
    listenToPlcMqtt(topic) {
      if (!this.client || !this.client.connected || !topic) return;

      if (this.currentSubscribedTopic && this.currentSubscribedTopic !== topic) {
        this.doUnsubscribe(this.currentSubscribedTopic);
      }

      this.currentSubscribedTopic = topic;
      this.doSubscribe(topic);
      this.client.removeAllListeners("message");

      this.client.on("message", (msgTopic, message) => {
        if (msgTopic !== this.currentSubscribedTopic) return;

        try {
          const decodedMessage = JSON.parse(message);
          if (!decodedMessage) return;

          // CASO 1: OMRON FINS
          if (decodedMessage?.fins?.request?.address && Array.isArray(decodedMessage?.read?.data)) {
            this.processOmronFins(decodedMessage);
            return;
          }

          // CASO 2: SIEMENS S7 BUFFER
          if (decodedMessage?.s7 || (decodedMessage?.db !== undefined && Array.isArray(decodedMessage?.data))) {
            this.processSiemensBuffer(decodedMessage);
            return;
          }

          // CASO 3: DICCIONARIO CLAVE-VALOR (Siemens / Node-RED)
          const dictCandidate =
            decodedMessage.payload || decodedMessage.data || decodedMessage.values || decodedMessage;

          if (typeof dictCandidate === "object" && dictCandidate !== null && !Array.isArray(dictCandidate)) {
            this.processKeyValuePayload(dictCandidate);
          }
        } catch (e) {
          console.error("Error al procesar mensaje MQTT:", e);
        }
      });
    },

    processOmronFins(decodedMessage) {
      const addressObj = decodedMessage?.fins?.request?.address || {};
      let rawArea = String(addressObj.MemoryArea || "").trim().toUpperCase();
      const rawAddress = addressObj.Address;
      const data = decodedMessage?.read?.data;

      if (!Array.isArray(data) || data.length === 0) return;

      // 1. Normalizar el prefijo de área (C -> CIO, DM -> D, etc.)
      let normArea = rawArea;
      if (normArea === "C" || normArea.startsWith("CIO")) normArea = "CIO";
      if (normArea === "DM" || normArea === "D") normArea = "D";
      if (normArea === "WR" || normArea === "W") normArea = "W";
      if (normArea === "HR" || normArea === "H") normArea = "H";
      if (normArea === "AR" || normArea === "A") normArea = "A";

      // 2. Extraer palabra y bit inicial
      let startWord = 0;
      let startBit = 0;

      const addrStr = String(rawAddress !== undefined ? rawAddress : "0").trim();
      if (addrStr.includes(".")) {
        const parts = addrStr.split(".");
        startWord = parseInt(parts[0], 10) || 0;
        startBit = parseInt(parts[1], 10) || 0;
      } else {
        startWord = parseInt(addrStr, 10) || 0;
      }

      if (addressObj.Bit !== undefined && addressObj.Bit !== null) {
        startBit = parseInt(addressObj.Bit, 10) || 0;
      }

      // =========================================================================
      // DISCRIMINADOR INTELIGENTE DE TIPO DE MENSAJE:
      // =========================================================================
      // A. Si algún byte es > 1, es imposible que sean bits (los bits solo valen 0 o 1).
      const hasNonBitBytes = data.some((b) => b > 1);

      // B. Comprobar si las variables de tu tabla para esta memoria son bits (contienen punto, ej: D1200.01)
      const isTargetingBits = this.variables.some((v) => {
        const a = this.normalizeAddress(v.formatted_address || v.address);
        return a.startsWith(`${normArea}${startWord}.`);
      });

      // Es trama de bits puros si NO tiene bytes > 1 y apunta a bits
      const isBitStream =
        !hasNonBitBytes &&
        (rawArea.includes("BIT") ||
          addrStr.includes(".") ||
          parseInt(addressObj.Bit, 10) > 0 ||
          isTargetingBits);

      // =========================================================================
      // CASO A: TRAMA DE BITS (1 byte por bit: 0x00 o 0x01) -> ej: D1200.01, CIO3944.00
      // =========================================================================
      if (isBitStream) {
        for (let k = 0; k < data.length; k++) {
          const totalBit = startBit + k;
          const currentWord = startWord + Math.floor(totalBit / 16);
          const bitInWord = totalBit % 16;
          const bitVal = data[k] === 1 || data[k] === true ? 1 : 0;

          const keyPad = `${normArea}${currentWord}.${String(bitInWord).padStart(2, "0")}`;
          const keyRaw = `${normArea}${currentWord}.${bitInWord}`;

          this.updateVariableValue(keyPad, bitVal);
          this.updateVariableValue(keyRaw, bitVal);
        }
        return; // Termina aquí porque este mensaje era exclusivamente de bits
      }

      // =========================================================================
      // CASO B: TRAMA DE PALABRAS / REALES (4 bytes por Float32 CDAB) -> ej: D52, D54
      // =========================================================================
      for (let i = 0; i < data.length; i += 2) {
        const wordOffset = i / 2;
        const currentWordAddress = startWord + wordOffset;
        const wordBase = `${normArea}${currentWordAddress}`;

        const byteH = data[i] || 0;
        const byteL = data[i + 1] !== undefined ? data[i + 1] : 0;

        const wordUint16 = (byteH << 8) | byteL;
        const wordInt16 = (wordUint16 & 0x8000) ? wordUint16 - 0x10000 : wordUint16;

        // Decodificación de REAL de 32 bits (Float32 orden CDAB estándar Omron)
        let floatVal = null;
        let dintVal = null;

        if (i + 3 < data.length) {
          const A = data[i];
          const B = data[i + 1];
          const C = data[i + 2];
          const D = data[i + 3];

          const buf = new ArrayBuffer(4);
          const view = new DataView(buf);
          view.setUint8(0, C);
          view.setUint8(1, D);
          view.setUint8(2, A);
          view.setUint8(3, B);

          floatVal = view.getFloat32(0, false);
          dintVal = view.getInt32(0, false);

          this.updateVariableValue(wordBase, floatVal);
        } else {
          this.updateVariableValue(wordBase, wordInt16);
        }

        // Asignar a las variables tipo palabra o entero
        for (const v of this.variables) {
          const targetAddr = this.normalizeAddress(v.formatted_address || v.address);
          const vType = (v.type || "REAL").toUpperCase();

          if (targetAddr === wordBase) {
            if (vType.includes("INT") && !vType.includes("DINT")) {
              this.$set(v, "value", wordInt16);
            } else if (vType.includes("WORD")) {
              this.$set(v, "value", wordUint16);
            } else if (vType.includes("DINT")) {
              this.$set(v, "value", dintVal !== null ? dintVal : wordInt16);
            } else if (!vType.includes("BIT") && !vType.includes("BOOL")) {
              this.$set(v, "value", floatVal !== null ? floatVal : wordInt16);
            }
          }
        }
      }
    },

    processSiemensBuffer(decodedMessage) {
      const s7Meta = decodedMessage.s7 || decodedMessage;
      const dbNumber = s7Meta.db !== undefined ? s7Meta.db : (s7Meta.dbNumber || 1);
      const startByte = s7Meta.start !== undefined ? s7Meta.start : (s7Meta.byteOffset || 0);
      const data = decodedMessage.data || s7Meta.data;

      if (!Array.isArray(data)) return;

      const dbPrefix = `DB${dbNumber}.`;

      for (let i = 0; i < data.length; i++) {
        const currentByte = startByte + i;
        const byteVal = data[i];

        for (let b = 0; b < 8; b++) {
          const bitVal = (byteVal >> b) & 1;
          this.updateVariableValue(`${dbPrefix}DBX${currentByte}.${b}`, bitVal);
          this.updateVariableValue(`DB${dbNumber},X${currentByte}.${b}`, bitVal);
        }

        if (i + 1 < data.length) {
          const wordUint = (data[i] << 8) | data[i + 1];
          const wordInt = (wordUint & 0x8000) ? wordUint - 0x10000 : wordUint;
          this.updateVariableValue(`${dbPrefix}DBW${currentByte}`, wordInt);
          this.updateVariableValue(`DB${dbNumber},INT${currentByte}`, wordInt);
        }

        if (i + 3 < data.length) {
          const buf = new ArrayBuffer(4);
          const view = new DataView(buf);
          view.setUint8(0, data[i]);
          view.setUint8(1, data[i + 1]);
          view.setUint8(2, data[i + 2]);
          view.setUint8(3, data[i + 3]);

          const floatVal = view.getFloat32(0, false);
          this.updateVariableValue(`${dbPrefix}DBD${currentByte}`, floatVal);
          this.updateVariableValue(`DB${dbNumber},REAL${currentByte}`, floatVal);
        }
      }
    },

    processKeyValuePayload(dict) {
      for (const [rawKey, rawVal] of Object.entries(dict)) {
        if (rawVal === null || rawVal === undefined) continue;

        let val = rawVal;
        if (typeof rawVal === "boolean") {
          val = rawVal ? 1 : 0;
        } else if (typeof rawVal === "string" && !isNaN(Number(rawVal))) {
          val = Number(rawVal);
        }

        this.updateVariableValue(rawKey, val);

        if (rawKey.includes(",")) {
          this.updateVariableValue(rawKey.replace(/,/g, "."), val);
        }
        if (rawKey.includes(".DBD")) {
          this.updateVariableValue(rawKey.replace(".DBD", ",REAL"), val);
        }
        if (rawKey.includes(".DBX")) {
          this.updateVariableValue(rawKey.replace(".DBX", ",X"), val);
        }
      }
    },

    updateVariableValue(addrKey, val) {
      if (!addrKey) return;
      const normKey = this.normalizeAddress(addrKey);
      this.$set(this.latestMemoryValues, normKey, val);

      // Si es CIO3944.00, también guardamos la variante 3944.00 por si alguien la definió sin CIO
      let altKey = null;
      if (normKey.startsWith("CIO")) {
        altKey = normKey.substring(3);
      } else if (normKey.startsWith("D")) {
        altKey = "DM" + normKey.substring(1);
      }

      for (const v of this.variables) {
        const normFmt = this.normalizeAddress(v.formatted_address);
        const normAddr = this.normalizeAddress(v.address);

        if (
          normFmt === normKey ||
          normAddr === normKey ||
          (altKey && (normFmt === altKey || normAddr === altKey))
        ) {
          this.$set(v, "value", val);
        }
      }
    },

    formatLiveValue(val) {
      if (val === null || val === undefined) return "—";
      if (typeof val === "boolean") return val ? "1" : "0";
      if (typeof val === "number") {
        return Number.isInteger(val) ? val.toString() : val.toFixed(2);
      }
      return val;
    },

    hasHistory(item) {
      return item.segundos !== null && item.segundos !== undefined && Number(item.segundos) > 0;
    },

    isVariableActive(item) {
      if (item.active === undefined || item.active === null) return true;
      return item.active === true || item.active === 1 || item.active === "1" || item.active === "t";
    },

    async fetchPlcs() {
      this.loadingPlcs = true;
      try {
        const response = await this.getData({ path: "variables/plcs" });
        this.plcs = Array.isArray(response) ? response.map((r) => r.value) : response.data || [];

        if (this.plcs.length > 0) {
          this.selectedPlcId = this.plcs[0].id_plc;
          await this.fetchVariables(this.selectedPlcId);
        }
      } catch (error) {
        console.error("Error al cargar los PLCs:", error);
        this.showNotification("Error al cargar la lista de PLCs", "error");
      } finally {
        this.loadingPlcs = false;
      }
    },

    async onPlcChange(plcId) {
      this.selectedVariables = [];
      this.modifiedVariableIds.clear();
      this.latestMemoryValues = {};
      if (!plcId) {
        this.variables = [];
        return;
      }
      await this.fetchVariables(plcId);
    },

    async fetchVariables(plcId) {
      this.loadingVariables = true;
      this.variables = [];
      this.selectedVariables = [];
      try {
        const response = await this.getData({
          path: `variables/plc/${plcId}`,
        });

        const rawVars = Array.isArray(response.variables) ? response.variables : response.data || [];

        this.variables = rawVars
          .map((v) => ({
            ...v,
            uid: `var_${v.id}`,
            is_new: false,
            value: this.getLiveValueFor(v.formatted_address, v.address),
            active: this.isVariableActive(v),
          }))
          .sort((a, b) => Number(b.active) - Number(a.active));

        if (this.currentPlc && this.currentPlc.topic_read) {
          this.listenToPlcMqtt(this.currentPlc.topic_read);
        }
      } catch (error) {
        console.error(`Error al cargar variables para el PLC ${plcId}:`, error);
        this.showNotification("Error al obtener las variables del PLC", "error");
      } finally {
        this.loadingVariables = false;
      }
    },

    getTypeColor(type) {
      if (!type) return "grey";
      const upper = type.toUpperCase();
      if (upper.includes("BOOL") || upper.includes("BIT")) return "indigo";
      if (upper.includes("INT") || upper.includes("DINT") || upper.includes("WORD")) return "blue";
      if (upper.includes("REAL") || upper.includes("FLOAT")) return "purple";
      if (upper.includes("STRING")) return "teal";
      return "blue-grey";
    },
  },
};
</script>

<style scoped>
.address-chip {
  background-color: #f0f4f8;
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
  font-size: 0.85rem;
  letter-spacing: 0.5px;
}

.color-circle {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 1px solid rgba(0, 0, 0, 0.2);
  display: inline-block;
  flex-shrink: 0;
}

.live-chip {
  font-size: 0.9rem;
  min-width: 90px;
  justify-content: flex-end;
}

.editable-cell {
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.editable-cell:hover {
  background-color: rgba(25, 118, 210, 0.08);
}

.edit-hint-icon {
  opacity: 0;
  transition: opacity 0.2s;
}

.editable-cell:hover .edit-hint-icon {
  opacity: 1;
}

/* Filas nuevas en borrador */
::v-deep .new-draft-row {
  background-color: #fff8e1 !important;
  border-left: 4px solid #ffa000 !important;
}

::v-deep .new-draft-row:hover {
  background-color: #ffecb3 !important;
}

/* Filas existentes editadas localmente */
::v-deep .modified-row {
  background-color: #e3f2fd !important;
  border-left: 4px solid #1976d2 !important;
}

::v-deep .modified-row:hover {
  background-color: #bbdefb !important;
}
</style>